/**
 * Partner Portal — Documents page (Feb 2026 · SCWT-BULK-KIT-DOCS-PORTAL).
 *
 * Surfaces every accounting document the partner owns. Today this
 * means Tax Invoices for paid bulk-kit orders (both new orders and
 * historical ones that pre-date the receipt PDF subsystem). The
 * page is deliberately category-aware so the same shell can carry
 * other document types later (e.g. commission statements, W-9-style
 * onboarding docs) without a rebuild.
 *
 * Data source: `GET /api/partner/documents` → { items, count }.
 * Each item:
 *   {
 *     category:       "bulk_kit_tax_invoice",
 *     receipt_number: "RCT-BKO-...",
 *     order_number:   "BKO-...",
 *     order_id:       "internal-id",
 *     paid_at:        ISO,
 *     total_cents:    int,
 *     download_url:   "/api/partner/documents/bulk-kit-tax-invoice/{id}.pdf",
 *     email_sent_at:  ISO or null,
 *   }
 *
 * Downloads: `window.open(API_BASE + download_url)` — the browser
 * forwards the partner's auth cookie. Cross-partner access is
 * blocked at the API layer (order ID guessing returns 404).
 */
import { useEffect, useState } from "react";
import { FileText, Download, Loader2, Receipt, Info } from "lucide-react";
import { PartnerShell } from "@/pages/partner/Dashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const CENTS = (n) => `$${((n || 0) / 100).toFixed(2)}`;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-AU",
      { day: "numeric", month: "short", year: "numeric" });
  } catch { return String(iso).slice(0, 10); }
}

// Reusable label + icon per document category — future-proofs
// the page for non-invoice docs without a redesign.
const CATEGORY_META = {
  bulk_kit_tax_invoice: {
    label: "Tax Invoice",
    icon: Receipt,
    accent: "text-cyan-800 bg-cyan-50 border-cyan-200",
  },
};

export default function PartnerDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/partner/documents");
        if (!cancelled) setDocs(data.items || []);
      } catch {
        if (!cancelled) toast.error("Couldn't load your documents.");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const openPdf = (doc) => {
    window.open(`${API_BASE}${doc.download_url}`, "_blank");
  };

  return (
    <PartnerShell active="documents">
      <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="partner-documents-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-cyan-700" /> Documents
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Tax invoices for every paid bulk-kit order on your account —
            ready to drop straight into Xero, MYOB or your bookkeeper&apos;s inbox.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-16 text-slate-500 flex flex-col items-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin" /> Loading your documents…
          </div>
        ) : docs.length === 0 ? (
          <div
            data-testid="partner-documents-empty"
            className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center"
          >
            <FileText className="w-10 h-10 text-slate-400 mx-auto mb-2" />
            <div className="font-semibold text-slate-700">No documents yet</div>
            <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
              As soon as your first bulk-kit order is paid, its
              Tax Invoice will land here — plus a copy in your email inbox.
            </div>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="partner-documents-list">
            {docs.map((d) => {
              const meta = CATEGORY_META[d.category] || CATEGORY_META.bulk_kit_tax_invoice;
              const Icon = meta.icon;
              return (
                <li
                  key={d.order_id}
                  data-testid={`partner-document-row-${d.receipt_number}`}
                  className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4 hover:border-cyan-300 hover:shadow-sm transition"
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={`shrink-0 mt-0.5 rounded-lg p-2 border ${meta.accent}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="font-semibold text-slate-900">
                          {meta.label}
                        </span>
                        <span
                          className="font-mono text-xs text-slate-600"
                          data-testid={`partner-document-receipt-no-${d.receipt_number}`}
                        >
                          {d.receipt_number}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Bulk Kit Order&nbsp;
                        <span className="font-mono text-slate-700">
                          {d.order_number}
                        </span>
                      </div>
                      <div className="text-xs text-slate-600 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                        <span>Paid&nbsp;
                          <span className="font-semibold text-slate-800">
                            {fmtDate(d.paid_at)}
                          </span>
                        </span>
                        <span>Total&nbsp;
                          <span className="font-semibold text-slate-800">
                            {CENTS(d.total_cents)}
                          </span>
                        </span>
                        {d.email_sent_at && (
                          <span className="text-slate-500">
                            Emailed {fmtDate(d.email_sent_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => openPdf(d)}
                    data-testid={`partner-document-download-${d.receipt_number}`}
                    className="shrink-0 border-cyan-300 text-cyan-800 hover:bg-cyan-50"
                  >
                    <Download className="w-4 h-4 mr-1.5" /> Download PDF
                  </Button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex gap-2 text-xs text-slate-600">
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-slate-400" />
          <div>
            Each Tax Invoice reflects what you actually paid at the time of
            purchase. Prices are locked to your original receipt — later price
            changes never affect an old invoice.
          </div>
        </div>
      </div>
    </PartnerShell>
  );
}
